import React from 'react'
import {
  Stack,
  Typography,
} from '@mui/material'

import AnswerActions from './AnswerActions'

import { Props } from './types'

export const CODE_LABEL = 'Question'

export const SUGGESTED_ANSWER_LABEL = 'Suggested answer'

export const SNIPPETS_LABEL = 'What the policy says'

export const NO_SNIPPETS_MESSAGE = 'No snippets available'

export const PREDICTION_LABELS: Record<string, string> = {
  no: 'No',
  unclear: 'Unclear',
  yes: 'Yes',
}

/**
 * Gibson's schema constrains a prediction to `yes | no | unclear`, but the column it
 * arrives through is a free string, so anything unrecognised is shown verbatim rather
 * than silently relabelled.
 */
export const predictionLabel = (prediction: string): string => PREDICTION_LABELS[prediction.trim().toLowerCase()]
  ?? prediction

export const questionCardTestId = (code: string): string => `policy-evaluation-question-${code}`

/**
 * One evaluated question: what the analysis suggested, and the policy text it read that
 * out of. The quoted policy is the evidence the user can check the suggestion against,
 * and the buttons below it are where they record their own judgment.
 */
function QuestionCard({ answer }: Props): React.ReactElement {
  const {
    code,
    prediction,
    question,
    snippets,
  } = answer

  return (
    <Stack
      data-testid={questionCardTestId(code)}
      spacing={1.5}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 3,
        py: 2,
      }}
    >
      <Typography variant='subtitle2' color='text.secondary'>{CODE_LABEL} {code}</Typography>
      <Typography variant='h6' color='black'>{question}</Typography>
      {/*
        The suggestion is a callout rather than a filled answer button: a filled button
        reads as "already answered", so the recommendation has to be prominent here, on
        its own, and the buttons below left for the user's actual answer.
      */}
      <Stack
        data-testid='policy-evaluation-prediction'
        direction='row'
        alignItems='baseline'
        spacing={1}
        sx={{
          bgcolor: 'primary.background',
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
          borderRadius: 1,
          px: 2,
          py: 1.5,
        }}
      >
        {/* The trailing space keeps the text content reading "Suggested answer: Yes". */}
        <Typography variant='subtitle2' color='primary.dark'>{SUGGESTED_ANSWER_LABEL}:{' '}</Typography>
        <Typography variant='h6' color='primary.dark'>{predictionLabel(prediction)}</Typography>
      </Stack>
      <Stack spacing={1}>
        <Typography variant='subtitle2'>{SNIPPETS_LABEL}</Typography>
        {snippets.length ? (
          // Third-party text lifted out of an arbitrary web page: rendered as children so
          // React escapes it. Never `dangerouslySetInnerHTML`, whatever it looks like.
          <Stack
            component='ul'
            spacing={1}
            sx={{
              my: 0,
              pl: 3,
            }}
          >
            {snippets.map(snippet => (
              <Typography component='li' key={snippet}>{snippet}</Typography>
            ))}
          </Stack>
        ) : (
          <Typography color='text.secondary' data-testid='policy-evaluation-no-snippets'>
            {NO_SNIPPETS_MESSAGE}
          </Typography>
        )}
      </Stack>
      <AnswerActions answer={answer} />
    </Stack>
  )
}

export default React.memo(QuestionCard)
